import React, { Component } from 'react';
import { observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { formatDateTime } from 'core/util/dateUtils';

import { tabbedChartColors } from 'app/stores/colors/Colors';
import { Highcharts } from 'core/components';
import { greekIt } from 'app/util/utils';
import { getMetricOption } from 'app/util/policies';
import { lineClassNames } from 'app/views/alerting/components/charts/AlertChart';
import { ALERT_BASELINE_REASONS } from '@kentik/ui-shared/alerting/constants';
import { parseBaselineUsed, MATCH_STATES, getChartTimeRange, getAlertActiveBand, getAlertBoundaryLines } from './utils';

function newSeries(name, data = [], options = {}) {
  return {
    name,
    data,
    marker: { symbol: 'circle', radius: 4 },
    ...options
  };
}

const StyledChart = styled(Highcharts)`
  .highcharts-axis-labels {
    font-size: 11px;
  }

  .highcharts-plot-band.no-outline {
    stroke-dasharray: none;
    stroke-width: 0;
  }

  .baseline-series .highcharts-graph {
    stroke-dasharray: 8, 6;
  }

  .baseline-fallback-series .highcharts-graph {
    stroke-dasharray: 2, 6;
  }
`;

@withTheme
@observer
export default class AlertDebugChart extends Component {
  static defaultProps = {
    data: []
  };

  handleChartCallback = (chart) => {
    this.chart = chart;
    this.resizeObserver.observe(chart.container);
  };

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  resizeObserver = new ResizeObserver(() => {
    this.chart.reflow();
  });

  // eslint-disable-next-line react/no-unused-class-component-methods
  formatTooltip({ x, y, colorIndex, point }) {
    const { theme } = this.props;

    if (!point) {
      return null;
    }

    const { unitLabel } = this.primaryMetric;
    const { chartColors } = this;
    const formattedDate = formatDateTime(x);
    const yDisplayValue = greekIt(y, { suffix: unitLabel, scaleMax: 0.999 }).displayFull;
    const pointColor = chartColors[colorIndex];
    const reasonLine = point.baselineReasonTitle
      ? `<br/><span style="color:${theme.colors.muted};">${point.baselineReasonTitle}</span>`
      : '';

    let deltaLine = '';
    if (Number.isFinite(point.delta)) {
      const sign = point.delta >= 0 ? '+' : '';
      const deltaDisplay = greekIt(point.delta, { suffix: unitLabel, scaleMax: 0.999 }).displayFull;
      const percentDisplay = Number.isFinite(point.deltaPercent) ? ` (${sign}${point.deltaPercent.toFixed(1)}%)` : '';
      deltaLine = `<br/><span style="color:${theme.colors.muted};">Δ ${sign}${deltaDisplay}${percentDisplay}</span>`;
    }

    return `${formattedDate}<br/><span style="color:${pointColor};">\u25CF</span> ${point.series.name}: <b>${yDisplayValue}</b>${reasonLine}${deltaLine}`;
  }

  get chartColors() {
    const { alertModel, theme } = this.props;
    const severityColor = theme.colors.severity[alertModel.severityKey];
    // Index 0 = severity color (matches/currentFallback/missingData series)
    // Index 1 = gray (baseline series)
    const baselineGray = theme.name === 'dark' ? 'rgb(144, 155, 166)' : 'rgb(128, 143, 154)';
    return [severityColor, baselineGray, ...tabbedChartColors.slice(2)];
  }

  get seriesData() {
    const { data = [] } = this.props;

    const series = data.map((trigger = {}) => {
      const { baseline_value, baseline_description, metrics = {} } = trigger?.context || {};
      const values = Object.entries(metrics).map(([metric, value]) => ({ metric, value }));

      return { values, time: trigger.timestamp, baselineUsed: baseline_description, baselineValue: baseline_value };
    });

    const dataGroups = {
      matches: newSeries('Current Value'),
      missingData: newSeries('Current Value (Missing Data)', [], { marker: { symbol: 'diamond', radius: 4 } }),
      skipped: newSeries('Skipped', [], { marker: { symbol: 'x', radius: 4 } }),
      baseline: newSeries('Baseline Value', [], {
        type: 'line',
        dashStyle: 'Dash',
        colorIndex: 1,
        className: 'baseline-series'
      }),
      baselineFallback: newSeries('Baseline Fallback', [], {
        type: 'line',
        dashStyle: 'ShortDot',
        colorIndex: 1,
        className: 'baseline-fallback-series'
      })
    };

    series.forEach((d) => {
      const { time, values, baselineValue, baselineUsed } = d;
      const { value: alertValue } = values.find(({ metric }) => metric === this.primaryMetric.value) || values[0] || {};
      const epochTime = Date.parse(time);
      const { matchState, currentIsFallback, baselineIsFallback } = parseBaselineUsed(baselineUsed);

      // When direction is historyToCurrent (ACT_CURRENT_*), current is B (comparator)
      // When direction is currentToHistory (ACT_BASELINE_*), baseline is B (comparator)
      const isInverseDirection = baselineUsed?.startsWith('ACT_CURRENT_');
      const reasonTitle = baselineUsed ? ALERT_BASELINE_REASONS[baselineUsed]?.title : undefined;

      const hasBothValues = Number.isFinite(alertValue) && Number.isFinite(baselineValue);
      const delta = hasBothValues ? alertValue - baselineValue : undefined;
      const deltaPercent = hasBothValues && baselineValue !== 0 ? (delta / baselineValue) * 100 : undefined;

      if (Number.isFinite(alertValue)) {
        const point = {
          x: epochTime,
          y: alertValue,
          baselineReasonTitle: isInverseDirection ? reasonTitle : undefined,
          delta,
          deltaPercent
        };
        if (currentIsFallback || matchState === MATCH_STATES.MATCH) {
          dataGroups.matches.data.push(point);
        } else if (matchState === MATCH_STATES.MISSING_DATA) {
          dataGroups.missingData.data.push(point);
        } else {
          dataGroups.skipped.data.push(point);
        }
      }

      if (Number.isFinite(baselineValue)) {
        const point = {
          x: epochTime,
          y: baselineValue,
          baselineReasonTitle: isInverseDirection ? undefined : reasonTitle
        };
        if (baselineIsFallback) {
          dataGroups.baselineFallback.data.push(point);
        } else {
          dataGroups.baseline.data.push(point);
        }
      }
    });

    return Object.values(dataGroups).filter((s) => s.data.length > 0);
  }

  get primaryMetric() {
    const { alertModel } = this.props;
    return getMetricOption(alertModel.primaryMetric, alertModel.reconMeasurement);
  }

  get plotBandsX() {
    const { alertModel } = this.props;
    const alertStartMs = Date.parse(alertModel.startTime);
    const alertEndMs = alertModel.endTime ? Date.parse(alertModel.endTime) : undefined;
    const band = getAlertActiveBand(alertStartMs, alertEndMs);
    return band ? [band] : [];
  }

  get plotLinesX() {
    const { alertModel } = this.props;
    const alertStartMs = Date.parse(alertModel.startTime);
    const alertEndMs = alertModel.endTime ? Date.parse(alertModel.endTime) : undefined;
    return getAlertBoundaryLines(alertStartMs, alertEndMs);
  }

  get plotLinesY() {
    const { alertModel } = this.props;
    const { conditions = [] } = alertModel.threshold;
    const { value: fallbackValue } = conditions.find((condition) => condition.fallbackSettings) || {};
    const primaryCondition = conditions.find(
      ({ type, metric }) => type === 'static' && metric === this.primaryMetric.value
    );

    const plotLines = [];
    let staticThreshold;

    if (primaryCondition) {
      staticThreshold = primaryCondition.comparisonValue;
    }

    if (staticThreshold && primaryCondition?.metric && primaryCondition.metric.includes('Percent')) {
      staticThreshold /= 1000;
    }

    if (Number.isFinite(staticThreshold)) {
      const severityClass = lineClassNames[alertModel.severityKey] ?? 'major-level';
      plotLines.push({
        id: 'static-threshold-line',
        className: `thin-line ${severityClass}`,
        label: {
          text: 'Static Threshold',
          align: 'left',
          x: -1,
          className: `highcharts-plot-line-label ${severityClass}`
        },
        value: staticThreshold,
        zIndex: 5
      });
    }

    if (Number.isFinite(fallbackValue)) {
      plotLines.push({
        id: 'fallback-line',
        className: 'baseline-level baseline-insight-level',
        label: {
          text: 'Fallback Value',
          align: 'left',
          x: -1,
          className: 'highcharts-plot-line-label baseline-insight-level'
        },
        value: fallbackValue,
        width: 1
      });
    }

    return plotLines;
  }

  get plotBandsY() {
    const { alertModel } = this.props;
    const { minTrafficValue } = alertModel.policyObject;

    if (!Number.isFinite(minTrafficValue) || minTrafficValue <= 0) {
      return [];
    }

    return [
      {
        id: 'policy-min-traffic-band',
        from: 0,
        to: minTrafficValue,
        color: 'rgba(201, 57, 26, 0.08)',
        label: {
          text: 'Below Min Traffic',
          align: 'right',
          x: -1,
          className: 'highcharts-plot-line-label baseline-insight-level'
        },
        zIndex: 0
      }
    ];
  }

  get requestedTimeRange() {
    const { alertModel, lookbackSeconds } = this.props;
    return getChartTimeRange(alertModel.startTime, lookbackSeconds);
  }

  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      chart: {
        type: 'scatter',
        zoomType: 'x'
      },
      yAxis: {
        title: { text: `Values (${this.primaryMetric.label})` },
        min: 0,
        maxPadding: 0.15,
        plotLines: this.plotLinesY,
        plotBands: this.plotBandsY,
        endOnTick: false
      },
      xAxis: {
        type: 'datetime',
        min: this.requestedTimeRange.minMs,
        max: this.requestedTimeRange.maxMs,
        crosshair: true,
        plotBands: this.plotBandsX,
        plotLines: this.plotLinesX,
        labels: {
          formatter() {
            const formattedTime = formatDateTime(this.value, 'HH:mm');

            if (formattedTime === '00:00') {
              return formatDateTime(this.value, 'D-MMM');
            }

            return formatDateTime(this.value, 'HH:mm');
          }
        }
      },
      tooltip: {
        formatter() {
          return self.formatTooltip(this);
        }
      },
      series: this.seriesData
    };
  }

  render() {
    return (
      <StyledChart
        callback={this.handleChartCallback}
        options={this.chartOptions}
        colors={this.chartColors}
        allowChartUpdate
      />
    );
  }
}
